import { MoneyText } from '@/components/MoneyText';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Link as MuiLink,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { payeesApi, type Payee, type PayeeDetail, type PayeeOrderRow } from '@/api/payees';
import { CategoryChip } from '@/components/CategoryChip';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { TabularText } from '@/components/TabularText';
import { civilToday } from '@/features/audit/dates';
import { addCivilDays } from '@/shared/servedWindow';
import { formatCivilDate, formatCivilMonth, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { destructiveItemSx } from '@/shell/objectMenu';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { activeCount, civilDateParam, enumParam, useUrlState } from '@/shared/useUrlState';
import { useNotify } from '@/shell/NotifyProvider';
import { resolveTokens } from '@/theme/theme';
import { downloadBlob } from '@/utils/downloadBlob';
import { slug } from '@/utils/slug';
import { AliasesTab } from './AliasesTab';
import { PayeeBarChart } from './PayeeBarChart';
import { PayeeEditDialog } from './PayeeEditDialog';
import { PayeeTransactions } from './PayeeTransactions';

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; detail: PayeeDetail }
  | { status: 'error'; error: unknown };

const TAB_LABEL_KEYS = {
  overview: 'payee.tabOverview',
  transactions: 'payee.tabTransactions',
  aliases: 'payee.aliases',
} as const satisfies Record<PayeeTab, string>;
const tabId = (tab: PayeeTab) => `payee-tab-${tab}`;
const panelId = (tab: PayeeTab) => `payee-panel-${tab}`;

function defaultRange(timeZone: string): { from: string; to: string } {
  const to = civilToday(timeZone);
  return { from: addCivilDays(to, -365), to };
}

const RANGE = ['from', 'to'] as const;

type PayeeTab = 'overview' | 'transactions' | 'aliases';
const TABS = ['overview', 'transactions', 'aliases'] as const satisfies readonly PayeeTab[];

/**
 * Payee detail `/me/payees/:payeeId` — totals and breakdowns are served as-is (no client
 * arithmetic). TMMAPP-0123 §D: one header with the page kebab (Edit, Merge…, Archive); the date
 * range carries the summary stats; Overview (three bar charts), Transactions and Aliases tabs
 * (`?tab=`, by replace). The payee's settings (Keep out of AI, what members see, the category
 * defaults) live in the Edit dialog; the "Kept out of AI" chip in the header is the visible state.
 * The kebab's Download transactions is the payee's lines in the selected range, as `.xlsx`
 * (TMMAPP-0124 item 5).
 *
 * Loads take a ticket, so an answer for a range that has since changed is dropped (item 20). A
 * quiet re-read after a write that fails keeps the page (and an open dialog) and says so inline
 * (item 16).
 */
function PayeeDetailView() {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const navigate = useNavigate();
  const notify = useNotify();
  const { payeeId: rawId } = useParams();
  const payeeId = Number(rawId);
  const currency = me.user.currency;
  const readOnly = me.tier !== 'SUBSCRIBER';
  // The date range is in the URL (`from` / `to`), omitted while it is the last 365 days; the tab
  // too, omitted on Overview.
  const timezone = me.user.timezone ?? 'UTC';
  const schema = useMemo(() => {
    const defaults = defaultRange(timezone);
    const options = { allowEmpty: true };
    return {
      from: civilDateParam(defaults.from, options),
      to: civilDateParam(defaults.to, options),
      tab: enumParam<PayeeTab>(TABS, 'overview'),
    };
  }, [timezone]);
  const [url, setUrl] = useUrlState(schema);
  const tab = url.tab;
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeCandidates, setMergeCandidates] = useState<Payee[]>([]);
  const [mergeInto, setMergeInto] = useState<Payee | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const loadTicket = useRef(0);
  // A payee is on screen (a quiet re-read's failure keeps it).
  const shown = useRef(false);

  /** `quiet`: re-read without the loading skeleton (after a write, so an open dialog stays). */
  const load = useCallback(
    async (cursor?: string, quiet = false) => {
      if (!Number.isFinite(payeeId)) {
        setState({ status: 'error', error: undefined });
        return;
      }
      // A page of more lines continues the current read; anything else starts a new one.
      const ticket = cursor === undefined ? ++loadTicket.current : loadTicket.current;
      if (cursor === undefined && !quiet) {
        shown.current = false;
        setState({ status: 'loading' });
      }
      // A new read supersedes a page still in flight: Load more is offered again for its answer.
      setLoadingMore(cursor !== undefined);
      try {
        const detail = await payeesApi.get(payeeId, {
          from: url.from,
          to: url.to,
          ...(cursor !== undefined ? { cursor } : {}),
        });
        if (ticket !== loadTicket.current) return;
        shown.current = true;
        setRefreshFailed(false);
        setState((prev) => {
          if (cursor !== undefined && prev.status === 'ready') {
            return {
              status: 'ready',
              detail: {
                ...detail,
                lines: [...prev.detail.lines, ...detail.lines],
              },
            };
          }
          return { status: 'ready', detail };
        });
      } catch (error) {
        if (ticket !== loadTicket.current) return;
        // A quiet re-read or a further page keeps what is shown, and says so.
        if ((quiet || cursor !== undefined) && shown.current) setRefreshFailed(true);
        else setState({ status: 'error', error });
      } finally {
        if (cursor !== undefined && ticket === loadTicket.current) setLoadingMore(false);
      }
    },
    [payeeId, url.from, url.to],
  );
  const [order, setOrder] = useState<PayeeOrderRow[] | null>(null);
  const loadOrder = useCallback(async () => {
    try {
      setOrder(await payeesApi.order());
    } catch {
      /* the header badge is omitted when the order cannot be read */
    }
  }, []);
  const refresh = useCallback(async () => {
    await Promise.all([load(undefined, true), loadOrder()]);
  }, [load, loadOrder]);

  useEffect(() => {
    queueMicrotask(() => {
      void load();
      void loadOrder();
    });
  }, [load, loadOrder]);

  const payee = state.status === 'ready' ? state.detail.payee : null;
  const title = usePageTitle({
    title: payee?.name ?? (state.status === 'loading' ? null : t('pageHeader.fallback.payee')),
    fallback: t('pageHeader.fallback.payee'),
  });
  // "← Counterparties", its list parent (TMM-B7 1.4).
  const back = { label: t('nav.payees'), to: '/me/payees' };

  // Unsaved work is guarded where it lives (TMM-B5): the edit, alias and move dialogs guard
  // themselves. The merge is a `ConfirmDialog`.
  const closeMerge = () => {
    setMergeInto(null);
    setMergeOpen(false);
  };

  async function handleDownload() {
    try {
      const { blob, filename } = await payeesApi.exportXlsx(payeeId, {
        from: url.from,
        to: url.to,
      });
      downloadBlob(blob, filename ?? `${slug(payee?.name ?? '', 'payee')}.xlsx`);
      notify(t('exports.ready'));
    } catch {
      notify(t('exports.failed'));
    }
  }

  async function handleArchive() {
    setBusy(true);
    setArchiveError(null);
    try {
      await payeesApi.archive(payeeId);
      setArchiveOpen(false);
      navigate('/me/payees');
    } catch {
      setArchiveError(t('accounts.errorGeneric'));
      setBusy(false);
    }
  }

  async function openMerge() {
    setMergeError(null);
    setMergeOpen(true);
    try {
      const rows = await payeesApi.list({ status: 'ACTIVE' });
      setMergeCandidates(rows.filter((row) => row.id !== payeeId));
    } catch {
      setMergeCandidates([]);
    }
  }

  async function handleMerge() {
    if (mergeInto === null) return;
    setBusy(true);
    setMergeError(null);
    try {
      await payeesApi.merge(payeeId, mergeInto.id);
      setMergeOpen(false);
      navigate(`/me/payees/${mergeInto.id}`);
    } catch (err) {
      // A known failure (permission, network, …) says so; anything else is the merge's own fallback.
      setMergeError(errorCopy(t, err, 'payee.mergeError', { name: payee?.name ?? '' }));
      setBusy(false);
    }
  }

  // Every state renders the same `PageHeader` first, so the `h1` (and route focus on it) survives
  // loading → ready / error.
  if (state.status === 'loading') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <Skeleton variant="rounded" height={240} />
      </>
    );
  }
  if (state.status === 'error' || payee === null) {
    return (
      <>
        <PageHeader title={title} back={back} />
        <ErrorState
          error={state.status === 'error' ? state.error : undefined}
          what={t('errors.what.payee')}
          onRetry={() => void load()}
        />
      </>
    );
  }

  const detail = state.detail;
  const totals = detail.totals;

  const menu = !readOnly ? (
    <OverflowMenu label={t('payee.actions')} title={payee.name} variant="page">
      {(close) => {
        const pick = (action: () => void) => () => {
          close();
          action();
        };
        return [
          <MenuItem key="edit" onClick={pick(() => setEditOpen(true))}>
            {t('common.edit')}
          </MenuItem>,
          <MenuItem key="download" onClick={pick(() => void handleDownload())}>
            {t('payee.download')}
          </MenuItem>,
          <MenuItem key="merge" onClick={pick(() => void openMerge())}>
            {t('payee.mergeMenu')}
          </MenuItem>,
          <MenuItem
            key="archive"
            onClick={pick(() => {
              setArchiveError(null);
              setArchiveOpen(true);
            })}
            sx={destructiveItemSx(tokens)}
          >
            {t('payee.archive')}
          </MenuItem>,
        ];
      }}
    </OverflowMenu>
  ) : undefined;

  const stat = (key: string, label: string, value: ReactNode) => (
    <Box key={key} data-payee-stat={key} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        component="div"
        sx={{ color: tokens.textLabel('bg'), whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
      <Typography component="div" variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );

  const overview = (
    <Box
      data-payee-overview=""
      sx={{
        display: 'grid',
        gap: 4,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
        alignItems: 'start',
      }}
    >
      <PayeeBarChart
        title={t('payee.byEnvelope')}
        columnLabel={t('payee.chart.envelope')}
        emptyText={t('payee.chart.emptyEnvelope')}
        currency={currency}
        rows={detail.byEnvelope.map((row) => ({
          key: String(row.envelopeId),
          label: row.name,
          labelText: row.name,
          amountMinor: row.amountMinor,
        }))}
      />
      <PayeeBarChart
        title={t('payee.byCategory')}
        columnLabel={t('payee.chart.category')}
        emptyText={t('payee.chart.emptyCategory')}
        currency={currency}
        rows={detail.byCategory.map((row) => ({
          key: `${row.categoryId}-${row.name}`,
          label: <CategoryChip id={row.categoryId} name={row.name} />,
          labelText: row.name,
          amountMinor: row.amountMinor,
        }))}
      />
      <Box sx={{ gridColumn: { lg: '1 / -1' } }}>
        <PayeeBarChart
          title={t('payee.byMonth')}
          columnLabel={t('payee.chart.month')}
          emptyText={t('payee.chart.emptyMonth')}
          currency={currency}
          // In time order, whatever order the server sent.
          rows={[...detail.byMonth]
            .sort((a, b) => a.month.localeCompare(b.month))
            .map((row) => ({
              key: row.month,
              label: formatCivilMonth(row.month, locale),
              labelText: formatCivilMonth(row.month, locale),
              amountMinor: row.amountMinor,
            }))}
        />
      </Box>
    </Box>
  );

  const orderPlace = order?.findIndex((row) => row.payeeId === payee.id) ?? -1;

  return (
    <>
      <PageHeader
        title={title}
        back={back}
        menu={menu}
        status={
          <>
            <Chip label={t(`payee.kind.${payee.kind}`)} />
            {orderPlace >= 0 && (
              <MuiLink
                component={RouterLink}
                to="/me/payees"
                data-payee-order-rank={orderPlace + 1}
              >
                {t('payee.aliasRank', { position: orderPlace + 1 })}
              </MuiLink>
            )}
            {payee.aiExcluded && <Chip color="warning" label={t('payee.aiExcludedChip')} />}
            {payee.linkedAccountName !== null && payee.linkedAccountId !== null && (
              <MuiLink component={RouterLink} to={`/me/accounts/${payee.linkedAccountId}`}>
                {payee.linkedAccountName}
              </MuiLink>
            )}
          </>
        }
      />
      <Stack spacing={3}>
        {payee.mergedIntoPayeeId !== null && (
          <Alert severity="info">
            <MuiLink component={RouterLink} to={`/me/payees/${payee.mergedIntoPayeeId}`}>
              {t('payee.mergedBanner')}
            </MuiLink>
          </Alert>
        )}

        {/* Item 23: the dates on the left, the range's stats beside them; below md they wrap under. */}
        <Box
          data-payee-range=""
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            columnGap: 4,
            rowGap: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5 }}>
            <TextField
              type="date"
              label={t('aiHistory.from')}
              value={url.from}
              onChange={(event) => setUrl({ from: event.target.value })}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="date"
              label={t('aiHistory.to')}
              value={url.to}
              onChange={(event) => setUrl({ to: event.target.value })}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <ClearFiltersButton
              active={activeCount(schema, url, RANGE)}
              onClear={() => setUrl(defaultRange(timezone))}
            />
          </Stack>
          <Box
            data-payee-stats=""
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, auto)' },
              columnGap: 3,
              rowGap: 1.5,
            }}
          >
            {stat(
              'total',
              t('payee.colTotal'),
              <MoneyText minor={totals.transactedMinor} currency={currency} />,
            )}
            {stat(
              'count',
              t('payee.colTransactions'),
              <TabularText>{formatNumber(totals.transactionCount, locale)}</TabularText>,
            )}
            {stat(
              'first',
              t('payee.colFirst'),
              totals.firstOn ? formatCivilDate(totals.firstOn, locale) : '—',
            )}
            {stat(
              'last',
              t('payee.colLast'),
              totals.lastOn ? formatCivilDate(totals.lastOn, locale) : '—',
            )}
          </Box>
        </Box>

        {detail.transfers.toLinkedAccountMinor !== 0 && payee.linkedAccountName !== null && (
          <Typography>
            <Trans
              i18nKey="payee.transfersLine"
              values={{ account: payee.linkedAccountName }}
              components={{
                amount: (
                  <MoneyText minor={detail.transfers.toLinkedAccountMinor} currency={currency} />
                ),
              }}
            />
          </Typography>
        )}

        {refreshFailed && (
          <Alert severity="error" data-payee-refresh-error="">
            {t('payee.refreshFailed')}
          </Alert>
        )}

        <Box>
          <Tabs
            value={tab}
            onChange={(_event, next: PayeeTab) => setUrl({ tab: next })}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label={t('payee.tabsLabel')}
            sx={{ mb: 2 }}
          >
            {TABS.map((value) => (
              <Tab
                key={value}
                value={value}
                id={tabId(value)}
                aria-controls={value === tab ? panelId(value) : undefined}
                label={t(TAB_LABEL_KEYS[value])}
              />
            ))}
          </Tabs>
          {/* Item 19: the shown tab's panel is named by its tab. */}
          <Box role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
            {tab === 'overview' && overview}
            {tab === 'transactions' && (
              <PayeeTransactions
                payee={payee}
                lines={detail.lines}
                currency={currency}
                readOnly={readOnly}
                hasMore={detail.nextCursor !== null}
                loadingMore={loadingMore}
                onLoadMore={() => {
                  if (detail.nextCursor !== null && !loadingMore) void load(detail.nextCursor);
                }}
                onChanged={refresh}
                onNotify={notify}
              />
            )}
            {tab === 'aliases' && (
              <AliasesTab payee={payee} readOnly={readOnly} onChanged={refresh} onNotify={notify} />
            )}
          </Box>
        </Box>

        {editOpen && (
          <PayeeEditDialog
            open={editOpen}
            payee={payee}
            categoryDefaults={detail.categoryDefaults}
            onClose={() => setEditOpen(false)}
            onSaved={refresh}
            onNotify={notify}
          />
        )}

        <ConfirmDialog
          open={archiveOpen}
          title={t('payee.archiveTitle', { name: payee.name })}
          consequence={t('payee.archiveConfirm')}
          confirmLabel={t('payee.archivePayee')}
          destructive
          busy={busy}
          error={archiveError}
          onConfirm={() => void handleArchive()}
          onCancel={() => setArchiveOpen(false)}
        />

        {/* A confirmation (TMM-B4): the target is its parameter, so no discard question. */}
        <ConfirmDialog
          open={mergeOpen}
          title={t('payee.mergeTitle', { name: payee.name })}
          consequence={t('payee.mergeConfirm', { name: payee.name })}
          confirmLabel={t('payee.merge')}
          destructive
          busy={busy}
          error={mergeError}
          confirmDisabled={mergeInto === null}
          onConfirm={() => void handleMerge()}
          onCancel={closeMerge}
        >
          <Autocomplete
            options={mergeCandidates}
            getOptionLabel={(option) => option.name}
            value={mergeInto}
            onChange={(_e, value) => setMergeInto(value)}
            renderInput={(params) => <TextField {...params} label={t('payee.mergeInto')} />}
          />
        </ConfirmDialog>
      </Stack>
    </>
  );
}

/** Keyed per id, so a hop to another counterparty (merge, the merged banner) starts from a fresh load. */
export function PayeePage() {
  const { payeeId } = useParams();
  return <PayeeDetailView key={payeeId} />;
}
